import { ScoreParameterType } from '../../api/services/qualityControlService'

export interface FieldSuggestion {
  name: string
  suggestedType: ScoreParameterType
  matrixFieldName?: string
  enumChoices?: string[]
}

function mapType(type: string, inputType?: string): ScoreParameterType | null {
  if (type === 'boolean' || type === 'checkbox') return 'boolean'
  if (type === 'rating' || type === 'expression') return 'numeric'
  if (type === 'text' && inputType === 'number') return 'numeric'
  if (type === 'radiogroup' || type === 'dropdown' || type === 'imagepicker') return 'enum'
  return null
}

function normalizeChoices(choices: unknown[]): string[] {
  return choices.map((c) => (typeof c === 'string' ? c : (c as { value: string }).value))
}

function processElements(elements: unknown[], matrixFieldName?: string): FieldSuggestion[] {
  const results: FieldSuggestion[] = []

  for (const el of elements) {
    if (!el || typeof el !== 'object') continue
    const q = el as Record<string, unknown>
    const type = q.type as string
    const name = q.name as string
    if (!type || !name) continue

    if (type === 'panel') {
      if (Array.isArray(q.elements)) {
        results.push(...processElements(q.elements, matrixFieldName))
      }
      continue
    }

    if (type === 'matrixdynamic') {
      if (Array.isArray(q.columns)) {
        const cols = (q.columns as Record<string, unknown>[]).map((col) => ({
          ...col,
          type: col.cellType ?? 'text',
        }))
        results.push(...processElements(cols, name))
      }
      continue
    }

    const suggestedType = mapType(type, q.inputType as string | undefined)
    if (!suggestedType) continue

    const suggestion: FieldSuggestion = {
      name,
      suggestedType,
      ...(matrixFieldName ? { matrixFieldName } : {}),
    }

    if (suggestedType === 'enum' && Array.isArray(q.choices)) {
      suggestion.enumChoices = normalizeChoices(q.choices as unknown[])
    }

    results.push(suggestion)
  }

  return results
}

export function parseSurveyFields(formJson: string): FieldSuggestion[] {
  try {
    const schema = JSON.parse(formJson) as Record<string, unknown>

    if (Array.isArray(schema.pages)) {
      const elements = (schema.pages as Record<string, unknown>[]).flatMap((p) =>
        Array.isArray(p.elements) ? (p.elements as unknown[]) : [],
      )
      return processElements(elements)
    }

    if (Array.isArray(schema.elements)) {
      return processElements(schema.elements as unknown[])
    }

    return []
  } catch {
    return []
  }
}
